import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Autocomplete, TextField } from "@mui/material";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { StoppageDto } from "../../../DTOs/Stoppage/StoppageDto";
import { stoppagesAjax } from "../../../ajax/StoppagesAjax";
import { DataTableFilteredRequest } from "../../../components/DataTable/TableHook";
import { OptionType } from "../../../types/OptionType";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { RootState } from "../../../state/store";

type BreakdownCodeCellProps = {
  stoppage: StoppageDto
  handleStoppageSubTypeChange: (stoppage: StoppageDto, stoppageSubTypeId: number) => Promise<void>
};

const BreakdownCodeCell: React.FC<BreakdownCodeCellProps> = ({ stoppage, handleStoppageSubTypeChange }) => {
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const [isStoppageSubTypeOptionsLoading, setIsStoppageSubTypeOptionsLoading] = useState(false);
  const [stoppageSubTypeOptions, setStoppageSubTypeOptions] = useState<OptionType<number>[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isExpanded) return;
    setIsStoppageSubTypeOptionsLoading(true);

    const fetchAll = async () => {
      try {
        const stoppageSubTypeOptionsResponse = await stoppagesAjax.getStoppageSubtypes({
          stoppageTypeId: stoppage.stoppageTypeId,
          pageNumber: 0,
          pageSize: 100
        } as DataTableFilteredRequest);

        if (!ErrorResponse.isErrorResponse(stoppageSubTypeOptionsResponse))
          setStoppageSubTypeOptions(stoppageSubTypeOptionsResponse.data.map((x) => ({
            value: x.stoppageSubTypeId ?? 0,
            label: x.stoppageSubType!
          } as OptionType<number>)));
      } catch (e) {
        // handle or ignore
      } finally {
        setIsStoppageSubTypeOptionsLoading(false);
      }
    };

    fetchAll();
  }, [stoppage.stoppageTypeId, stoppage.stoppageSubTypeId, isExpanded]);

  return (
    <Autocomplete
      disabled={isShiftLocked}
      size="small"
      sx={{ width: '100%' }}
      options={stoppageSubTypeOptions}
      loading={isStoppageSubTypeOptionsLoading}
      getOptionLabel={(option) => option.label}
      value={stoppageSubTypeOptions.find((x) => x.value === stoppage.stoppageSubTypeId) ?? { value: stoppage.stoppageSubTypeId, label: stoppage.stoppageSubType?.stoppageSubType || '' }}
      onOpen={() => setIsExpanded(true)}
      onClose={() => setIsExpanded(false)}
      onChange={async (_, val) => {
        if (!val || !val.value) return;

        setIsStoppageSubTypeOptionsLoading(true);
        await handleStoppageSubTypeChange(stoppage, val.value);
        setIsStoppageSubTypeOptionsLoading(false);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Stoppage Type"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {(isStoppageSubTypeOptionsLoading) && (
                    <span className="animate-fade-in"><FontAwesomeIcon icon={faSpinner} spin /></span>
                  )}
                  {params.slotProps.input.endAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  )
}

export default BreakdownCodeCell